/*
 *  			'License for ONE AXA SOFTWARE - Inner Source'
 * 					L O A S - I S
 * 
 * The following License aims to be applied between the AXA Companies, according to the following definition:
 * "AXA Companies" means AXA, Société Anonyme with a “AXA Companies ” Board of Directors having its principal offices at 25, avenue Matignon, 75008 Paris, registered on the Commercial Registry of Paris under the number 572 093 920; and (i) any other company controlled by, or controlling AXA, with a company being considered as controlling another: (a) when it holds directly or indirectly a portion of the capital according to it the majority of the voting rights in general meetings of shareholders of this company; (b) when it holds solely the majority of the voting rights in this company by virtue of an agreement concluded with other partners or shareholders and which is not contrary to the interest of the company; (c) when it determines de facto, by voting rights which it holds, the decisions in the general meetings of shareholders of this company; (d) in any event, when it holds, directly or indirectly, a portion of voting rights greater than 40% and when no other partner or shareholder holds directly or indirectly a portion which is greater than its own; (ii) any economic interest group in which AXA and/or one or more other Companies of the AXA Group participates for at least 50% in operating costs; (iii) in the cases where the law applicable to a company limits voting rights or control (such as defined here in above), this company will be deemed to be a company of the AXA Group, if the voting rights in 2 Binding Corporate Rules – AXA Group – version 2013 general shareholders’ meetings or the control held by a Company of the AXA Group reaches the maximum amount fixed by said applicable law; and (iv) all AXA Companies constitute the “AXA Group” (hereafter "One AXA").  (Binding Corporate Rules - AXA Group - version 2019).
 * 
 * Software credits: 
 * 	Creator: AXA GO GETD
 * 	Main contributors: AXA GO GETD
 * 
 * Permission is hereby granted, free of charge, to any person or entity within One AXA obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software within One AXA, including the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
 * 
 * The above credit notice and this permission notice shall be included in all copies or substantial portions of the Software.
 * Any improvement or modification of the Software shall be made available to the whole One AXA, under the terms of this License.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

// File for constants configuration

import { MESSAGES } from '@translations/labels';
import { LANGUAGES } from '@languages';
import logger from '@utils/logger';

const labels = {
  ...MESSAGES.header,
  ...MESSAGES.manageBot,
  ...MESSAGES.conversationHistory,
  ...MESSAGES.commons,
};

export const idField = '_id';
export const retryField = '_retry';
export const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:3010/api';
export const baseURLWs = baseURL.replace('/api', '');

logger.debug('baseURL:', baseURL);

export const SIZES = {
  small: 'small',
  medium: 'medium',
  large: 'large',
  xlarge: 'xlarge',
};

export const ACCENT = {
  ok: 'ok',
  warning: 'warning',
  ko: 'ko',
};

export const COLORS = {
  primary: 'primary',
  secondary: 'secondary',
  terciary: 'terciary',
};

export const PRIORITIES = [
  {
    name: 'high',
    value: 10,
  },
  {
    name: 'normal',
    value: 5,
  },
  {
    name: 'low',
    value: 1,
  },
];

export const STATUS = [
  {
    name: 'OK',
    values: ['RECEIVED', 'POSSIBLE_HIT', 'CONFIRMED_HIT', 'HIT'],
  },
  {
    name: 'CHECK',
    values: ['LIGHT_FALSE_POSITIVE', 'LOW_SCORE'],
  },
  {
    name: 'FIX',
    values: ['NO_MATCH', 'INCOMPLETE', 'FALSE_POSITIVE'],
  },
];

export const STATUS_FILTER = STATUS.map(({ name }) => ({
  text: name,
  value: name,
}));

export const findLanguage = locale => {
  return LANGUAGES.find(language => locale === language.locale)?.language;
};

export const getLanguageFilter = locales => {
  return locales.map(locale => {
    const langName = findLanguage(locale);
    return { value: langName, text: langName };
  });
};

export const ISSUES = [
  {
    source: 'STEMS',
    type: 'WARNING',
    values: ['EQUIVALENT_UTTERANCES'],
  },
  {
    source: 'STEMS',
    type: 'ERROR',
    values: ['EQUIVALENT_UTTERANCES_DIFFERENT_INTENTS'],
  },
  {
    source: 'VOLUMETRY',
    type: 'WARNING',
    values: ['NOT_ENOUGH_UTTERANCES'],
  },
  {
    source: 'MISCLASSIFIED',
    type: 'WARNING',
    values: ['MISCLASSIFIED'],
  },
  {
    source: 'LOWSCORE',
    type: 'WARNING',
    values: ['LOW_SCORE'],
  },
];
export const ROUTES = {
  login: {
    label: labels.login,
    path: '/',
  },
  register: {
    label: labels.register,
    path: '/register',
  },
  dashboard: {
    label: labels.dashboard,
    path: '/dashboard',
  },
  users: {
    label: labels.users,
    path: '/users',
  },
  addUser: {
    label: labels.addUser,
    path: '/users/create',
  },
  editUser: {
    label: labels.editUser,
    path: '/users/:userId/edit',
  },
  conversationHistory: {
    label: labels.conversationHistory,
    path: '/conversation-history',
    byMessage: {
      label: labels.byMessage,
      path: '/conversation-history/by-message',
    },
    byIntent: {
      label: labels.byIntent,
      path: '/conversation-history/by-intent',
    },
    darkZone: {
      label: labels.darkZone,
      path: '/conversation-history/tech-zone',
    },
  },
  manageBot: {
    label: labels.manageBot,
    path: '/manage-the-bot',
    corpus: {
      label: 'Corpus',
      path: '/manage-the-bot/corpus',
    },
    domains: {
      label: labels.domains,
      path: '/manage-the-bot/corpus/domains',
    },
    newDomain: {
      label: labels.newDomainTitle,
      path: '/manage-the-bot/domains/create',
    },
    editDomain: {
      label: labels.editDomainTitle,
      path: '/manage-the-bot/domains/:domainId/edit',
    },
    readOnlyDomain: {
      label: labels.seeDomainTitle,
      path: '/manage-the-bot/domains/:domainId/view',
    },
    intentsByDomain: {
      label: labels.editDomainTitle,
      path: '/manage-the-bot/intents/by-domain/:domainId',
    },
    intents: {
      label: labels.intents,
      path: '/manage-the-bot/corpus/intents',
    },
    newIntent: {
      label: labels.newIntentTitle,
      path: '/manage-the-bot/intents/create',
    },
    directIntentLink: {
      label: labels.editIntentTitle,
      path: '/manage-the-bot/intents/:intentName/name',
    },
    editIntent: {
      label: labels.editIntentTitle,
      path: '/manage-the-bot/intents/:intentId/edit',
    },
    readOnlyIntent: {
      label: labels.seeIntentTitle,
      path: '/manage-the-bot/intents/:intentId/view',
    },
    lastTraining: {
      label: labels.analysisReport,
      path: '/manage-the-bot/corpus/last-training',
    },
    releases: {
      label: labels.releases,
      path: '/manage-the-bot/releases',
    },
    botConnection: {
      label: labels.botConnection,
      path: '/manage-the-bot/bot-settings',
    },
    vdnSettings: {
      label: labels.vdnSettings,
      path: '/manage-the-bot/vdn-settings',
    },
    manageFlow: {
      label: labels.flows,
      path: '/manage-the-bot/flows-management',
    },
    newFlow: {
      label: labels.flows,
      path: '/manage-the-bot/flows/create',
    },
    editFlow: {
      label: labels.flows,
      path: '/manage-the-bot/flows/:flowId/edit',
    },
    readOnlyFlow: {
      label: labels.flows,
      path: '/manage-the-bot/flows/:flowId/view',
    },
  },
  support: {
    label: labels.support,
    path: '/support',
  },
  unauthorized: {
    label: labels.unauthorized,
    path: '/unauthorized',
  },
};

export const KEY_CODES = {
  downArrow: 40,
  enter: 13,
  escape: 27,
  pageDown: 34,
  pageUp: 33,
  tab: 9,
  upArrow: 38,
};

export const NAVIGATION_KEYS = [
  KEY_CODES.escape,
  KEY_CODES.upArrow,
  KEY_CODES.downArrow,
  KEY_CODES.pageUp,
  KEY_CODES.pageDown,
];

export const AGENT_STATUS = {
  Trained: 'Trained',
  Training: 'Training',
  NeverTrained: 'Never Trained',
  TrainingFailed: 'Training Failed',
  Importing: 'Importing',
  OutOfDate: 'Out of date',
};

export const ANALYZER_STATUS = {
  Analyzing: 'Analyzing',
  Analyzed: 'Analyzed',
  AnalysisFailed: 'Analysis Failed',
  NeverAnalyzed: 'Never Analyzed',
  OutOfDate: 'Out of date',
  Idle: 'Idle',
};

export const IMPORT_STATUS = {
  Validating: 'Validating',
  ValidationErrors: 'Validation Errors',
  ImportingFailed: 'Importing Failed',
  Importing: 'Importing',
  Idle: 'Idle',
};

export const ANALYTICS_CASE = {
  USER_COMPLAINTS: 'userComplaints',
  MISSED_MESSAGES: 'missedMessages',
  UNUSED_INTENTS: 'unusedIntents',
};

export const AUTH = {
  LOCAL_LOGIN: 'basic',
  PASSAXA_LOGIN: 'passaxa',
  ONEACCOUNT_LOGIN: 'oneaccount',
};

export const NONE_INTENT = 'none';

export const INTENT_FILTER_OPTIONS = {
  INTENTS: 'By intent',
  EXAMPLES: 'By example',
  BOTH: 'Both',
};

export const IMPORT_FILES_STATUS = {
  UPLOADING: 'uploading',
  PROCESSING: 'processing',
  FINISHED: 'finished',
  ERROR: 'error',
};
